import {useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';

import {isIOS} from '@zzng/common/utils/agent';

import CardGroupCheck from '@/root/components/CardGroupCheck';
import {ROUTES} from '@/root/constants/routes';
import usePopup from '@/root/hooks/usePopup';
import {HomeCard} from '@/root/models/home';
import {homeKeys, useHomeCards} from '@/root/queries/home';

import PageLayout from '@/shared/components/PageLayout';
import Portal from '@/shared/components/Portal';
import {TIARA_ACTION, TIARA_PAGE_NAME} from '@/shared/constants/tiara';
import useAdvancedNavigate from '@/shared/hooks/useAdvancedNavigate';
import useHistoryBackAOS from '@/shared/hooks/useHistoryBackAOS';
import usePageTransition from '@/shared/hooks/usePageTransition';
import {StatusBarColorProps} from '@/shared/hooks/useStatusBarColor';
import useTiara from '@/shared/hooks/useTiara';
import useToast from '@/shared/hooks/useToast';

interface Props {
  statusBarColor: StatusBarColorProps;
}

const CardDeletePage = ({statusBarColor}: Props) => {
  const {trackEvent} = useTiara({pageName: TIARA_PAGE_NAME.CARD_DELETE});
  const {transitionTarget, leave} = usePageTransition<HTMLDivElement>();
  const navigate = useAdvancedNavigate();
  const {openPopup} = usePopup();
  const toast = useToast();
  const {cards, deleteCardMuation} = useHomeCards();
  const [selectedCardIds, setSelectedCardIds] = useState(new Set<string>());
  const queryClient = useQueryClient();

  const onCardDelete = () => {
    const cardIds = Array.from(selectedCardIds);
    const [feDeletePageTiaraCustomProps] = location.pathname.split('/').filter(Boolean);

    deleteCardMuation.mutate(cardIds, {
      onSuccess: () => {
        // 선택된 카드수만큼 티아라 로그 발생
        cardIds.forEach((cardId) => {
          const card = cards?.find((card) => card.groupedCards.map(({cardId}) => cardId).includes(cardId));
          if (!card) return;
          trackEvent({
            action: TIARA_ACTION.CARD_DELETE_CLICK,
            customProps: {package_id: card.key, fe_delete_page: feDeletePageTiaraCustomProps}
          });
        });
        // 간헐적으로 갱신 되지 않는 이슈를 해결하기 위해 직접 캐시 데이터 편집
        // https://jira.daumkakao.com/browse/CARD-629
        queryClient.setQueryData(homeKeys.cards(), (cards: HomeCard[]) =>
          cards
            .map((card) => ({
              ...card,
              groupedCards: card.groupedCards.filter(({cardId}) => !cardIds.includes(cardId))
            }))
            .filter((card) => card.groupedCards.length > 0)
        );

        queryClient.invalidateQueries({queryKey: homeKeys.recommendCards()});

        toast.open('디지털카드가 삭제되었습니다.');
        if (isIOS) {
          // 삭제 직후 위젯 데이터 갱신
          // https://jira.daumkakao.com/browse/CARD-797
          window.digitalCard?.updateWidgetData?.();
        }
        navigate(ROUTES.HOME, {keepSearchParams: true});
      },
      onError: () => {
        openPopup('CARD_DELETE_FAIL');
      }
    });
  };

  const onClickConfirm = () => {
    openPopup('CARD_DELETE', {onSubmit: onCardDelete, selectedCardCount: selectedCardIds.size});
  };

  const onChangeSelectedCards = (cardIds: Set<string>) => {
    setSelectedCardIds(new Set(cardIds));
  };

  const onClickBack = () => {
    leave({onAnimationEnd: () => navigate(-1)});
  };

  useHistoryBackAOS(() => {
    onClickBack();
  });

  return (
    <PageLayout
      tiara={{pageName: TIARA_PAGE_NAME.CARD_DELETE, pageAction: TIARA_ACTION.CARD_DELETE_VIEW}}
      className="container-delete"
      statusBarColor={statusBarColor}
      headerProps={{
        title: '삭제하기',
        buttons: [
          {type: 'BACK', onClick: onClickBack},
          {
            type: 'CONFIRM',
            startAdornment: selectedCardIds.size > 0 && <span className="txt_count">{selectedCardIds.size}</span>,
            disabled: selectedCardIds.size === 0 || deleteCardMuation.isPending,
            onClick: onClickConfirm
          }
        ]
      }}
      ref={transitionTarget}
    >
      <div className="inner_g">
        <h3 className="screen_out">카드 목록</h3>
        <div className="group_list type11">
          <ul className="list_g" role="list">
            {cards?.map((card, index) => (
              <CardGroupCheck
                key={`${card.key}-${index}`}
                card={card}
                selectedCardIds={selectedCardIds}
                onChangeSelectedCards={onChangeSelectedCards}
              />
            ))}
          </ul>
        </div>
      </div>
      <Portal isOpen={deleteCardMuation.isPending} type="LOADING" />
    </PageLayout>
  );
};

export default CardDeletePage;
